import type { ISiteRepository } from "../repositories/SiteRepository";
import type {
  Site,
  CreateSiteRequest,
  UpdateSiteRequest,
  SiteFilters,
  SiteListResponse,
} from "../pages/SiteManagement/types/siteManagement.types";
import { SITE_STATUS, isSiteStatus } from "@/types/siteStatus.type";
import {
  STUDY_ASSIGNMENT_STATUS,
  isStudyAssignmentUiStatus,
} from "@/types/studySiteAssignmentStatus.type";

export interface ISiteUseCase {
  createSite(site: CreateSiteRequest): Promise<Site>;
  getSites(
    filters?: SiteFilters,
    page?: number,
    limit?: number,
  ): Promise<SiteListResponse>;
  getAllSites(filters?: SiteFilters): Promise<Site[]>;
  getSiteById(id: string): Promise<Site>;
  updateSite(id: string, updates: UpdateSiteRequest): Promise<Site>;
  deleteSite(id: string): Promise<void>;
  activateSite(id: string): Promise<Site>;
  archiveSite(id: string): Promise<Site>;
  getSitesByStudy(studyId: string): Promise<Site[]>;
}

export class SiteUseCase implements ISiteUseCase {
  constructor(private siteRepository: ISiteRepository) {}

  async createSite(site: CreateSiteRequest): Promise<Site> {
    // Validate required fields
    if (!site.name.trim()) {
      throw new Error("Site name is required");
    }

    if (!site.location.address.trim()) {
      throw new Error("Site address is required");
    }

    if (!site.location.city.trim()) {
      throw new Error("Site city is required");
    }

    if (!site.location.country?.trim()) {
      throw new Error("Site country is required");
    }

    // if (!site.principalInvestigatorId?.trim()) {
    //   throw new Error("Principal investigator is required");
    // }

    // if (!site.siteCoordinatorId?.trim()) {
    //   throw new Error("Site coordinator is required");
    // }

    // if (!site.contactEmail?.trim()) {
    //   throw new Error("Contact email is required");
    // }

    // // Validate email format
    // const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // if (!emailRegex.test(site.contactEmail)) {
    //   throw new Error("Please provide a valid email address");
    // }

    // // Validate phone number if provided
    // if (site.contactPhone) {
    //   const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    //   if (!phoneRegex.test(site.contactPhone.replace(/[\s\-\(\)]/g, ""))) {
    //     throw new Error("Please provide a valid phone number");
    //   }
    // }

    try {
      const createdSite = await this.siteRepository.createSite(site);
      return createdSite;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to create site: ${error.message}`);
      }
      throw new Error("Failed to create site: Unknown error occurred");
    }
  }

  async getSites(
    filters?: SiteFilters,
    page = 1,
    limit = 10,
  ): Promise<SiteListResponse> {
    if (page < 1) {
      throw new Error("Page number must be greater than 0");
    }

    if (limit < 1 || limit > 100) {
      throw new Error("Limit must be between 1 and 100");
    }

    try {
      // Pass filters as-is to repository - let caller control filtering
      const result = await this.siteRepository.getSites(filters, page, limit);
      return result;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch sites: ${error.message}`);
      }
      throw new Error("Failed to fetch sites: Unknown error occurred");
    }
  }

  async getAllSites(filters?: SiteFilters): Promise<Site[]> {
    try {
      return await this.siteRepository.getAllSites(filters);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch all sites: ${error.message}`);
      }
      throw new Error("Failed to fetch all sites: Unknown error occurred");
    }
  }

  async getSiteById(id: string): Promise<Site> {
    if (!id?.trim()) {
      throw new Error("Site ID is required");
    }

    try {
      const site = await this.siteRepository.getSiteById(id);
      return site;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch site: ${error.message}`);
      }
      throw new Error("Failed to fetch site: Unknown error occurred");
    }
  }

  async updateSite(id: string, updates: UpdateSiteRequest): Promise<Site> {
    if (!id?.trim()) {
      throw new Error("Site ID is required");
    }

    if (!updates || Object.keys(updates).length === 0) {
      throw new Error("At least one field must be provided for update");
    }

    // Validate email format if provided
    // if (updates.contactDetails?.email) {
    //   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    //   if (!emailRegex.test(updates.contactDetails.email)) {
    //     throw new Error("Please provide a valid email address");
    //   }
    // }

    // // Validate phone number if provided
    // if (updates.contactDetails?.phone) {
    //   const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    //   if (
    //     !phoneRegex.test(
    //       updates.contactDetails.phone.replace(/[\s\-\(\)]/g, "")
    //     )
    //   ) {
    //     throw new Error("Please provide a valid phone number");
    //   }
    // }

    // // Validate that name is not empty if provided
    // if (updates.name !== undefined && !updates.name?.trim()) {
    //   throw new Error("Site name cannot be empty");
    // }

    // // Validate that principal investigator is not empty if provided
    // if (
    //   updates.principalInvestigator !== undefined &&
    //   !updates.principalInvestigator?.trim()
    // ) {
    //   throw new Error("Principal investigator cannot be empty");
    // }

    try {
      const updatedSite = await this.siteRepository.updateSite(id, updates);
      return updatedSite;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to update site: ${error.message}`);
      }
      throw new Error("Failed to update site: Unknown error occurred");
    }
  }

  async deleteSite(id: string): Promise<void> {
    if (!id?.trim()) {
      throw new Error("Site ID is required");
    }

    try {
      // First check if site exists and get its current status
      const site = await this.siteRepository.getSiteById(id);

      // Only allow deletion of Inactive sites
      if (isSiteStatus(site.status, SITE_STATUS.ACTIVE)) {
        throw new Error(
          "Cannot delete an active site. Please deactivate the site first.",
        );
      }

      if (site.assignedStudies && site.assignedStudies.length > 0) {
        const activeStudies =
          site.studyAssignments?.filter((assignment) =>
            isStudyAssignmentUiStatus(
              assignment.status,
              STUDY_ASSIGNMENT_STATUS.ACTIVE,
            ),
          ) || [];
        if (activeStudies.length > 0) {
          throw new Error(
            "Cannot delete site with active study assignments. Please complete or transfer studies first.",
          );
        }
      }

      await this.siteRepository.deleteSite(id);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to delete site: ${error.message}`);
      }
      throw new Error("Failed to delete site: Unknown error occurred");
    }
  }

  async activateSite(id: string): Promise<Site> {
    if (!id?.trim()) {
      throw new Error("Site ID is required");
    }

    try {
      // First check if site exists and get its current status
      const site = await this.siteRepository.getSiteById(id);

      if (isSiteStatus(site.status, SITE_STATUS.ACTIVE)) {
        throw new Error("Site is already active");
      }

      if (isSiteStatus(site.status, SITE_STATUS.DEACTIVATED)) {
        throw new Error(
          "Cannot activate a Deactivated site. Please contact support if needed.",
        );
      }

      const activatedSite = await this.siteRepository.activateSite(id);
      return activatedSite;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to activate site: ${error.message}`);
      }
      throw new Error("Failed to activate site: Unknown error occurred");
    }
  }

  async archiveSite(id: string): Promise<Site> {
    if (!id?.trim()) {
      throw new Error("Site ID is required");
    }

    try {
      // First check if site exists and get its current status
      const site = await this.siteRepository.getSiteById(id);

      if (isSiteStatus(site.status, SITE_STATUS.DEACTIVATED)) {
        throw new Error("Site is already Deactivated");
      }

      // Check if site has active studies
      if (site.assignedStudies && site.assignedStudies.length > 0) {
        const activeStudies =
          site.studyAssignments?.filter((assignment) =>
            isStudyAssignmentUiStatus(
              assignment.status,
              STUDY_ASSIGNMENT_STATUS.ACTIVE,
            ),
          ) || [];
        if (activeStudies.length > 0) {
          throw new Error(
            "Cannot archive site with active study assignments. Please complete or transfer studies first.",
          );
        }
      }

      const archivedSite = await this.siteRepository.archiveSite(id);
      return archivedSite;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to archive site: ${error.message}`);
      }
      throw new Error("Failed to archive site: Unknown error occurred");
    }
  }

  async getSitesByStudy(studyId: string): Promise<Site[]> {
    if (!studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    try {
      return await this.siteRepository.getSitesByStudy(studyId);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch sites for study: ${error.message}`);
      }
      throw new Error(
        "Failed to fetch sites for study: Unknown error occurred",
      );
    }
  }
}
